// CustomEditor.jsx
import React, { useState, useRef, useEffect } from 'react';
import ReactQuill from 'react-quill';
import {
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Tabs,
  Tab,
  Grid,
  Slider
} from '@mui/material';
import 'react-quill/dist/quill.snow.css';
import 'katex/dist/katex.min.css';
import katex from 'katex';
import renderMathInElement from 'katex/contrib/auto-render/auto-render';
import html2canvas from 'html2canvas';
import Typo from 'typo-js';

// Math categories for the math modal
const mathCategories = [
  {
    label: 'Greek',
    symbols: [
      '\\alpha', '\\beta', '\\gamma', '\\Gamma', '\\delta', '\\Delta', '\\epsilon', '\\zeta',
      '\\eta', '\\theta', '\\Theta', '\\kappa', '\\lambda', '\\Lambda', '\\mu', '\\nu',
      '\\pi', '\\Pi', '\\sigma', '\\Sigma', '\\phi', '\\Phi', '\\omega', '\\Omega',
    ],
  },
  {
    label: 'Fractions/Binom',
    symbols: [
      '\\frac{x}{y}',
      '\\binom{n}{k}',
      '\\frac{dy}{dx}',
      '\\sqrt{x}',
      '\\sum_{i=1}^n',
      '\\int_a^b',
      '\\lim_{x\\to0}',
      '\\sqrt[n]{x}',
    ],
  },
  // Add more categories as desired
];

const CustomEditor = ({ value, onChange }) => {
  const quillRef = useRef(null);
  const previousValueRef = useRef(value);

  // --- Math Modal State ---
  const [mathModalOpen, setMathModalOpen] = useState(false);
  const [latexValue, setLatexValue] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [imgScale, setImgScale] = useState(1.0);

  // --- Spell Check State ---
  const [typoInstance, setTypoInstance] = useState(null);
  const [misspelledWords, setMisspelledWords] = useState([]);
  // Mapping from misspelled word to chosen correction
  const [corrections, setCorrections] = useState({});
  const [spellModalOpen, setSpellModalOpen] = useState(false);

  // Handle onChange with deduplication
  const handleEditorChange = (content) => {
    // Only call onChange if the content actually changed
    if (content !== previousValueRef.current) {
      previousValueRef.current = content;
      onChange(content);
    }
  };

  // Update ref when value prop changes externally
  useEffect(() => {
    previousValueRef.current = value;
  }, [value]);

  // --- Quill Config ---
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ script: 'sub' }, { script: 'super' }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      ['clean'],
    ],
  };
  const quillFormats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'script',
    'list',
    'bullet',
    'link',
    'image',
  ];

  // --- Edit Mode Spell Checker Initialization ---
  useEffect(() => {
    async function loadDictionary() {
      try {
        const affResponse = await fetch('/dictionary/en_US.aff');
        const dicResponse = await fetch('/dictionary/en_US.dic');
        const affText = await affResponse.text();
        const dicText = await dicResponse.text();
        const typo = new Typo('en_US', affText, dicText, { platform: 'any' });
        setTypoInstance(typo);
      } catch (error) {
        console.error('Failed to load dictionary files:', error);
      }
    }
    loadDictionary();
  }, []);

  // --- Set Spell Check Attributes on Quill Editor ---
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (editor && editor.root) {
      editor.root.setAttribute('spellcheck', 'true');
      editor.root.setAttribute('autocorrect', 'on');
      editor.root.setAttribute('autocapitalize', 'on');
    }
  }, []);

  // --- Auto-render KaTeX in Quill ---
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      renderMathInElement(editor.root, {
        delimiters: [
          { left: '$$', right: '$$', display: true },
          { left: '$', right: '$', display: false },
        ],
        throwOnError: false,
      });
    }
  }, [value]);

  // --- Spell Check Functionality ---
  const runSpellCheck = () => {
    if (!typoInstance) return;
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const text = editor.getText();
    const words = text.split(/\s+/);
    const misspelled = [];
    const corr = {};
    words.forEach((word) => {
      const cleanWord = word.replace(/[^a-zA-Z']/g, '');
      if (cleanWord && !typoInstance.check(cleanWord)) {
        // Avoid duplicates (case insensitive)
        if (!misspelled.find(item => item.word.toLowerCase() === cleanWord.toLowerCase())) {
          const suggestions = typoInstance.suggest(cleanWord);
          misspelled.push({ word: cleanWord, suggestions });
          corr[cleanWord] = suggestions.length > 0 ? suggestions[0] : cleanWord;
        }
      }
    });
    setMisspelledWords(misspelled);
    setCorrections(corr);
    setSpellModalOpen(true);
  };

  const handleCorrectionChange = (word, newCorrection) => {
    setCorrections((prev) => ({ ...prev, [word]: newCorrection }));
  };

  const applySpellCorrections = () => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    let htmlContent = editor.root.innerHTML;
    // Replace each misspelled word with the chosen correction
    for (const word in corrections) {
      const suggestion = corrections[word];
      // Use word boundaries; this simple regex might not cover all cases.
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      htmlContent = htmlContent.replace(regex, suggestion);
    }
    editor.root.innerHTML = htmlContent;
    onChange(htmlContent);
    setSpellModalOpen(false);
  };

  // --- Math Modal Handlers ---
  const handleTabChange = (e, newValue) => setTabIndex(newValue);
  const handleSymbolClick = (symbol) => {
    setLatexValue((prev) => prev + symbol);
  };

  const handleInsertLatexAsImage = async () => {
    if (!latexValue.trim()) return;
    const hiddenDiv = document.createElement('div');
    hiddenDiv.style.position = 'fixed';
    hiddenDiv.style.left = '-9999px';
    hiddenDiv.style.display = 'inline-block';
    hiddenDiv.style.padding = '10px';
    hiddenDiv.style.backgroundColor = 'transparent';
    document.body.appendChild(hiddenDiv);
    try {
      katex.render(latexValue, hiddenDiv, { throwOnError: false, displayMode: false });
      const canvas = await html2canvas(hiddenDiv, { backgroundColor: null, scale: imgScale });
      const dataUrl = canvas.toDataURL('image/png');
      const editor = quillRef.current?.getEditor();
      if (editor) {
        const range = editor.getSelection(true);
        editor.insertEmbed(range.index, 'image', dataUrl, 'user');
        editor.setSelection(range.index + 1, 0, 'user');
      } else {
        onChange(value + `<img src="${dataUrl}" alt="math" />`);
      }
    } catch (error) {
      console.error('Error inserting LaTeX as image:', error);
    } finally {
      document.body.removeChild(hiddenDiv);
      setMathModalOpen(false);
    }
  };

  return (
    <Box>
      {/* Quill Editor */}
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={handleEditorChange}
        modules={quillModules}
        formats={quillFormats}
      />
      {/* Buttons below editor */}
      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
        <Button variant="outlined" onClick={runSpellCheck}>
          Spell Check
        </Button>
        <IconButton color="error" size="small" onClick={() => setMathModalOpen(true)}>
          <Typography variant="h5" sx={{ lineHeight: 1 }}>Σ</Typography>
        </IconButton>
      </Box>

      {/* Math Modal */}
      <Dialog open={mathModalOpen} onClose={() => setMathModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Insert Math as Image</DialogTitle>
        <DialogContent dividers>
          <Tabs value={tabIndex} onChange={handleTabChange}>
            {mathCategories.map((cat) => (
              <Tab key={cat.label} label={cat.label} />
            ))}
          </Tabs>
          <Box sx={{ mt: 2, maxHeight: 200, overflowY: 'auto' }}>
            <Grid container spacing={1}>
              {mathCategories[tabIndex].symbols.map((symbol) => (
                <Grid item key={symbol}>
                  <Button variant="outlined" size="small" onClick={() => handleSymbolClick(symbol)}>
                    {symbol}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>
          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
            Or type LaTeX manually (no $ delimiters needed):
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={latexValue}
            onChange={(e) => setLatexValue(e.target.value)}
            placeholder="e.g. \\frac{x}{y}"
          />
          <Box sx={{ mt: 3 }}>
            <Typography gutterBottom>Image Scale: {imgScale.toFixed(1)}</Typography>
            <Slider
              min={0.5}
              max={4.0}
              step={0.1}
              value={imgScale}
              onChange={(e, val) => setImgScale(val)}
              valueLabelDisplay="auto"
            />
          </Box>
          <Box sx={{ mt: 2, p: 1, border: '1px solid #ccc', borderRadius: 1 }}>
            <Typography variant="subtitle2">Preview:</Typography>
            <Box sx={{ mt: 1 }}>
              <span dangerouslySetInnerHTML={{ __html: renderLatexInline(latexValue) }} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMathModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleInsertLatexAsImage}>Insert</Button>
        </DialogActions>
      </Dialog>

      {/* Spell Check Modal */}
      <Dialog open={spellModalOpen} onClose={() => setSpellModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Spell Check Results</DialogTitle>
        <DialogContent dividers>
          {misspelledWords.length === 0 ? (
            <Typography>No misspelled words found!</Typography>
          ) : (
            misspelledWords.map((item, idx) => (
              <Box key={idx} sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>{item.word}</strong>:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  {item.suggestions.map((suggestion, j) => (
                    <Button
                      key={j}
                      variant={corrections[item.word] === suggestion ? "contained" : "outlined"}
                      size="small"
                      onClick={() => handleCorrectionChange(item.word, suggestion)}
                    >
                      {suggestion}
                    </Button>
                  ))}
                </Box>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSpellModalOpen(false)}>Cancel</Button>
          {misspelledWords.length > 0 && (
            <Button variant="contained" onClick={applySpellCorrections}>
              Replace with Selected
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/**
 * Renders LaTeX inline using KaTeX for the preview.
 */
function renderLatexInline(expr) {
  if (!expr.trim()) return '';
  try {
    return katex.renderToString(expr, { throwOnError: false, displayMode: false });
  } catch (err) {
    return `<span style="color:red;">Invalid LaTeX</span>`;
  }
}

export default CustomEditor;
